import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TablePagination } from '../../../components/common/TablePagination';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { useCasePermissions } from '../../../hooks/UseCasePermission';
import { useTableFilters } from '../../../hooks/UseTableFilter';
import {
  deletePloForm,
  fetchPloApplications,
} from '../../../services/PloFourService';
import type { User } from '../../../types/user';
import { formatDateToMyanmar } from '../../../utils/GlobalUtils';

export function PLOFormFourTable() {
  const navigate = useNavigate();

  const itemsPerPage = 10;
  const filters = useTableFilters();

  // Additional state for PTI Date Filters
  const [ptiFirstDate, setPtiFirstDate] = useState('');
  const [ptiSecondDate, setPtiSecondDate] = useState('');

  const [currentUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  useCasePermissions(currentUser);

  // Helper to verify if current user is the origin creator or belongs to the origin office
  const checkIsOriginUser = (app: any): boolean => {
    if (!currentUser) return false;

    // 1. Match by Office ID (Top-level originOfficeId or nested originOffice.id)
    const originOfficeId = app.originOfficeId || app.originOffice?.id || app.document?.originOfficeId || app.officeId;
    if (currentUser.officeId && originOfficeId && currentUser.officeId === originOfficeId) {
      return true;
    }

    // 2. Match by Creator User ID
    const createdById = app.createdById || app.createdBy?.id;
    if (currentUser.id && createdById && currentUser.id === createdById) {
      return true;
    }

    return false;
  };

  const { data, isLoading, isError, error, isPlaceholderData, refetch, isFetching } = useQuery({
    queryKey: [
      'ploFormFour',
      filters.debouncedSearch,
      filters.debouncedParty,
      filters.selectedStatus,
      ptiFirstDate,
      ptiSecondDate,
      filters.currentPage,
    ],
    queryFn: () =>
      fetchPloApplications({
        searchTerm: filters.debouncedSearch,
        partySearch: filters.debouncedParty,
        selectedStatus: filters.selectedStatus,
        ptiFirstDate,
        ptiSecondDate,
        currentPage: filters.currentPage,
        itemsPerPage,
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Safely extract applications array handling both direct array & wrapped { data: [...] } formats
  const rawList = Array.isArray(data) ? data : data?.data;
  const applications = Array.isArray(rawList) ? rawList : [];
  const totalItems = data?.totalItems || applications.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handleDelete = async (id?: string) => {
    if (!id) return;

    const confirmed = window.confirm('ဤမှတ်တမ်းကို ဖျက်ရန် သေချာပါသလား။');
    if (!confirmed) return;

    try {
      await deletePloForm(id);
      await refetch();
    } catch (err: any) {
      console.error('Delete failed:', err);
      alert(err?.message || 'ဖျက်ခြင်း မအောင်မြင်ပါ။');
    }
  };

  const handleClearAllFilters = () => {
    filters.handleClearFilters();
    setPtiFirstDate('');
    setPtiSecondDate('');
  };

  const hasActiveFilters =
    filters.hasActiveFilters || Boolean(ptiFirstDate) || Boolean(ptiSecondDate);

  return (
    <div className="space-y-4 bg-white p-4 md:p-6 border border-slate-200 shadow-sm">
      {/* Table Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            PLOပုံစံ (၄) စာရင်းများ
          </h2>
        </div>

        {/* Action Buttons Group */}
        <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center gap-1.5 border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            title="ဒေတာများ နောက်ဆုံး Data များ ရယူရန်"
          >
            <svg
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin text-blue-600' : 'text-slate-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            နောက်ဆုံး Data များ ရယူရန်
          </button>

          <button
            type="button"
            onClick={() => navigate('/plo-form-four/create')}
            className="inline-flex items-center justify-center gap-1.5 bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors shrink-0"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            ဖောင်အသစ်ထည့်ရန်
          </button>
        </div>
      </div>

      {/* Filter Controls Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end bg-slate-50/60 p-3 border border-slate-100">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အထွေထွေ ရှာဖွေရန်</label>
          <input
            type="text"
            placeholder="စာရွက်စာတမ်းနံပါတ်/အမှုနံပါတ်/ပုဒ်မ..."
            value={filters.searchTerm}
            onChange={(e) => {
              filters.setSearchTerm(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">နစ်နာသူ / သက်သေအမည်</label>
          <input
            type="text"
            placeholder="နစ်နာသူ / သက်သေအမည်"
            value={filters.partySearch}
            onChange={(e) => {
              filters.setPartySearch(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">PTI (၁) နေ့စွဲ</label>
          <FormDatePicker
            value={ptiFirstDate}
            onChange={(e) => {
              setPtiFirstDate(e.target.value);
              filters.setCurrentPage(1);
            }}
            name="ptiFirstDate"
            className="w-full"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">PTI (၂) နေ့စွဲ</label>
            <FormDatePicker
              value={ptiSecondDate}
              onChange={(e) => {
                setPtiSecondDate(e.target.value);
                filters.setCurrentPage(1);
              }}
              name="ptiSecondDate"
              className="w-full"
            />
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearAllFilters}
              title="စစ်ထုတ်မှုများ ဖျက်ရန်"
              className="mt-5 shrink-0 inline-flex items-center justify-center text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-200 py-1.5 px-2 bg-white transition-colors"
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-hidden border border-black bg-white shadow-sm relative">
        {isPlaceholderData && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-xs bg-black text-white px-2.5 py-1 shadow-sm">
              နောက်ဆုံးပေါ်အချက်အလက် ရယူနေပါသည်...
            </span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-[11px] min-w-[1350px]">
            <thead>
              <tr className="bg-slate-50 border-b border-black text-center align-middle font-bold text-black">
                <th rowSpan={2} className="border-r border-black p-2 w-10">စဉ်</th>
                <th colSpan={4} className="border-r border-black p-1 bg-slate-100/70">နစ်နာသူ / သက်သေအချက်အလက်</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-50">PTI ပြုလုပ်သည့်နေ့စွဲ</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-100/70">တွေ့ဆုံမေးမြန်းသူအချက်အလက်</th>
                <th colSpan={2} className="border-r border-black p-1 bg-slate-50">စီရင်ချက်အမိန့်</th>
                <th rowSpan={2} className="border-r border-black p-1 w-16">
                  အယူခံ / ပြင်ဆင်<br />
                  <span className="text-[9px] font-normal">၁။အယူခံ၊ ၂။ပြင်ဆင်</span>
                </th>
                <th rowSpan={2} className="border-r border-black p-1 w-24">မှတ်ချက်</th>
                <th rowSpan={2} className="p-1 w-20 font-sans text-black">လုပ်ဆောင်ချက်</th>
              </tr>

              <tr className="bg-slate-50 border-b border-black text-center align-middle text-[10px] font-bold text-black">
                <th className="border-r border-black p-1 w-14">၁။ကျား<br />၂။မ</th>
                <th className="border-r border-black p-1 w-20">
                  အသက်<br />
                  <span className="text-[8px] font-normal">၁။ကလေးသူငယ်၊ ၂။လူငယ်၊ ၃။လူကြီး</span>
                </th>
                <th className="border-r border-black p-1 w-24">နစ်နာသူ / သက်သေအမည်</th>
                <th className="border-r border-black p-1 w-20">သက်သေကြားနာမည့်နေ့</th>

                <th className="border-r border-black p-1 w-20">PTI ပထမအကြိမ် ပြုလုပ်သည့်နေ့</th>
                <th className="border-r border-black p-1 w-20">PTI ဒုတိယအကြိမ် ပြုလုပ်သည့်နေ့</th>

                <th className="border-r border-black p-1 w-24">တွေ့ဆုံမေးမြန်းသူ အမည်</th>
                <th className="border-r border-black p-1 w-32 text-left px-2">
                  ၁။ခရိုင်ဥပဒေအရာရှိ<br />
                  ၂။ဒုခရိုင်ဥပဒေအရာရှိ<br />
                  ၃။မြို့နယ်ဥပဒေအရာရှိ<br />
                  ၄။ဒုမြို့နယ်ဥပဒေအရာရှိ<br />
                  ၅။ဦးစီးအရာရှိ<br />
                  ၆။အခြား
                </th>

                <th className="border-r border-black p-1 w-28">
                  ၁။တရားသေလွတ်သည်<br />
                  ၂။တရားရှင်လွတ်သည်<br />
                  ၃။စစ်ဆေးစီရင်သည်
                </th>
                <th className="border-r border-black p-1 w-20">အမှုစစ် တရားသူကြီးအမည်</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-black bg-white">
              {isLoading && !isPlaceholderData ? (
                <tr>
                  <td colSpan={14} className="text-center py-12 text-xs text-black font-semibold">
                    အချက်အလက်များ ဆွဲယူနေပါသည်...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={14} className="text-center py-12 text-xs text-red-600 font-semibold">
                    {error instanceof Error ? error.message : 'အချက်အလက်များ ဆွဲယူရာတွင် အမှားအယွင်းရှိခဲ့ပါသည်။'}
                  </td>
                </tr>
              ) : applications.length > 0 ? (
                applications.map((app: any, index: number) => {
                  const formDetails = app.pLOFormFour || app.ploFormFour || app.formData || app;
                  const recordId = app.id || app._id || formDetails.id || formDetails._id;
                  const isOriginUser = checkIsOriginUser(app);

                  const formatDate = (dateVal?: string) =>
                    formatDateToMyanmar(dateVal) || '-';

                  return (
                    <tr key={recordId || index} className="hover:bg-slate-50 transition-colors text-center align-middle text-black">
                      <td className="border-r border-black p-1 font-bold">{index + 1}</td>
                      <td className="border-r border-black p-1">{formDetails.gender || '-'}</td>
                      <td className="border-r border-black p-1">{formDetails.ageGroup || '-'}</td>
                      <td className="border-r border-black p-1 font-medium">{formDetails.victimOrWitnessName || '-'}</td>
                      <td className="border-r border-black p-1">{formatDate(formDetails.interviewDate)}</td>
                      <td className="border-r border-black p-1">{formatDate(formDetails.ptiFirstDate)}</td>
                      <td className="border-r border-black p-1">{formatDate(formDetails.ptiSecondDate)}</td>
                      <td className="border-r border-black p-1">{formDetails.interviewerName || '-'}</td>
                      <td className="border-r border-black p-1">{formDetails.interviewerRole || '-'}</td>
                      <td className="border-r border-black p-1">{formDetails.judgmentType || '-'}</td>
                      <td className="border-r border-black p-1">{formDetails.officerOpinionName || formDetails.judgeName || '-'}</td>
                      <td className="border-r border-black p-1">{formDetails.appealOrRevision || '-'}</td>
                      <td className="border-r border-black p-1 text-left px-1.5">{formDetails.remarks || '-'}</td>

                      {/* Action Cell */}
                      <td className="p-1 text-center">
                        {isOriginUser ? (
                          <div className="flex justify-center items-center gap-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/plo-form-four/edit/${app.documentNumber}`)}
                              className="inline-flex items-center justify-center gap-0.5 border border-blue-600 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                              title="ပြင်ဆင်ရန်"
                            >
                              ✏️ ပြင်ရန်
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(app.documentNumber)}
                              className="inline-flex items-center justify-center border border-rose-600 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
                              title="ဖျက်ရန်"
                            >
                              🗑️
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-400 font-medium">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={14} className="text-center py-12 text-xs text-black font-semibold">
                    ကိုက်ညီသော အချက်အလက် ရှာမတွေ့ပါ။
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <TablePagination
            currentPage={filters.currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={filters.setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}